//
// Copyright 2024 DXOS.org
//

import { type Extension, Prec, RangeSetBuilder, StateEffect, StateField } from '@codemirror/state';
import {
  Decoration,
  type DecorationSet,
  EditorView,
  type KeyBinding,
  ViewPlugin,
  type ViewUpdate,
  keymap,
} from '@codemirror/view';

import { type PlaceholderOptions, modalStateField, placeholder } from '@dxos/ui-editor';
import { type Range } from '@dxos/ui-editor/types';
import { isNonNullable, isTruthy } from '@dxos/util';

const DELIMITERS = [' ', ':'];

export type PopoverOptions = {
  trigger?: string | string[];
  triggerKey?: string;
  placeholder?: Partial<PlaceholderOptions>;
  delimiters?: string[];

  /**
   * Open the popover automatically while typing a word (no trigger character required).
   * The completion range covers the word under the cursor (delimited by {@link PopoverOptions.delimiters}).
   */
  activateOnTyping?: boolean;

  /**
   * Delimiters after which an EMPTY token still opens the menu — `type:` offering every typename,
   * say. Opt-in per consumer because the empty-token guard is load-bearing elsewhere: `RefEditor`
   * uses `activateOnTyping` with no trigger, where an empty query would match every object.
   */
  activateOnDelimiters?: string[];

  /**
   * Triggers whose query may contain spaces (e.g. multi-word names). A double space, a leading space or a
   * line break still ends it, and Escape keeps the typed text.
   */
  multiWordTriggers?: string[];

  // Trigger update.
  onTextChange?: (event: { view: EditorView; pos: number; text: string; trigger?: string }) => void;
  onClose?: (event: { view: EditorView }) => void;

  // Menu specific.
  /** Returns true when the menu consumed the key (an item was selected); false falls through. */
  onEnter?: (event: { view: EditorView }) => boolean | void;
  onArrowUp?: (event: { view: EditorView }) => void;
  onArrowDown?: (event: { view: EditorView }) => void;
};

/**
 * Creates a popover that appears when the trigger character is inserted.
 * This can be used for context menus or autocompletion.
 */
export const popover = (options: PopoverOptions = {}): Extension => {
  return [
    Prec.highest(popoverKeymap(options)),
    popoverStateField,
    popoverTriggerListener(options),
    (options.activateOnTyping || options.activateOnDelimiters?.length) && popoverAutoActivate(options),
    popoverAnchorDecoration(options),
    modalStateField,
    options.trigger &&
      placeholder({
        // TODO(burdon): Translations.
        content: `Press '${Array.isArray(options.trigger) ? options.trigger[0] : options.trigger}' for commands`,
        focusOnly: true,
        ...options.placeholder,
      }),
  ].filter(isTruthy);
};

/**
 * Listen for selection and document changes.
 */
const popoverTriggerListener = (options: PopoverOptions) =>
  EditorView.updateListener.of(({ view, docChanged }) => {
    const { range: activeRange, trigger } = view.state.field(popoverStateField) ?? {};
    if (!activeRange) {
      return;
    }

    const text = view.state.doc.sliceString(activeRange.from, activeRange.to);
    const query = trigger ? text.slice(1) : text;
    const selection = view.state.selection.main;
    const shouldClose =
      // Trigger deleted.
      (trigger ? trigger !== text[0] : false) ||
      // Whitespace in text; a multi-word query may hold single spaces between words.
      (trigger && options.multiWordTriggers?.includes(trigger) ? /^\s|[^\S ]| {2}/.test(query) : /\s/.test(query)) ||
      // Cursor moved before the range.
      selection.head < activeRange.from ||
      // Cursor moved after the range (+1 to handle selection changing before doc).
      selection.head > activeRange.to + 1;

    const nextRange = shouldClose ? null : docChanged ? { from: activeRange.from, to: selection.head } : activeRange;
    if (nextRange !== activeRange) {
      view.dispatch({
        effects: popoverRangeEffect.of(nextRange ? { range: nextRange, trigger } : null),
      });
    }

    if (shouldClose) {
      options.onClose?.({ view });
    }
  });

/**
 * Open the popover while typing a word, without requiring a trigger character. Once a range is
 * active, {@link popoverTriggerListener} keeps it in sync and closes it (whitespace, cursor moves).
 */
const popoverAutoActivate = (options: PopoverOptions) =>
  EditorView.updateListener.of(({ view, docChanged, transactions }) => {
    // Only user input opens the popover — programmatic syncs (e.g. a controlled `value` update on
    // mount) must not pop the menu.
    if (!docChanged || !transactions.some((tr) => tr.isUserEvent('input')) || view.state.field(popoverStateField)) {
      return;
    }
    const selection = view.state.selection.main;
    if (!selection.empty) {
      return;
    }
    const line = view.state.doc.lineAt(selection.head);
    const before = line.text.slice(0, selection.head - line.from);
    const idx = getLastIndexOf(before, options.delimiters ?? DELIMITERS);
    const token = before.slice(idx + 1);
    // Two independent reasons to open: mid-word under `activateOnTyping`, or immediately after a
    // delimiter that has a list behind it. They are separate because opening on every word character
    // moves focus to the popover on each keystroke, which is intolerable in an editor whose menu is
    // empty for most positions.
    const onDelimiter = token.length === 0 && (options.activateOnDelimiters ?? []).includes(before[idx]);
    if (!onDelimiter && (!options.activateOnTyping || token.length === 0)) {
      return;
    }
    view.dispatch({
      effects: popoverRangeEffect.of({ range: { from: line.from + idx + 1, to: selection.head } }),
    });
  });

/**
 * Popover navigation.
 */
const popoverKeymap = (options: PopoverOptions) => {
  const triggers = Array.isArray(options.trigger) ? options.trigger : [options.trigger];
  return keymap.of(
    [
      // Prefix triggers.
      ...triggers.filter(isNonNullable).map((trigger) => ({
        key: trigger,
        run: (view: EditorView) => {
          // Determine if we should trigger the popover:
          // 1. Empty lines or at the beginning of a line.
          // 2. When there's a preceding space.
          const selection = view.state.selection.main;
          const line = view.state.doc.lineAt(selection.head);
          if (
            line.text.trim() === '' ||
            selection.head === line.from ||
            (selection.head > line.from && line.text[selection.head - line.from - 1] === ' ')
          ) {
            // Insert and select the trigger.
            view.dispatch({
              changes: { from: selection.head, insert: trigger },
              selection: { anchor: selection.head + 1, head: selection.head + 1 },
              effects: popoverRangeEffect.of({ trigger, range: { from: selection.head, to: selection.head + 1 } }),
            });

            return true;
          }

          return false;
        },
      })),

      //
      // Custom trigger.
      //
      options.triggerKey &&
        ({
          key: options.triggerKey,
          run: (view: EditorView) => {
            const selection = view.state.selection.main;
            const line = view.state.doc.lineAt(selection.head);

            // Get last word.
            let str = line.text.slice(0, selection.head - line.from);
            const idx = getLastIndexOf(str, options.delimiters ?? DELIMITERS);
            if (idx !== -1) {
              str = str.slice(idx + 1);
            }

            // Create anchor even if zero length (append space).
            const from = line.from + idx;
            view.dispatch({
              effects: popoverRangeEffect.of({ range: { from: from + 1, to: selection.head } }),
              changes:
                selection.head === view.state.doc.length
                  ? { from: from + 1, to: selection.head, insert: ' ' }
                  : undefined,
            });
            return true;
          },
        } satisfies KeyBinding),

      //
      // Nav keys.
      //
      {
        key: 'ArrowUp',
        run: (view: EditorView) => {
          const range = view.state.field(popoverStateField)?.range;
          if (range) {
            options.onArrowUp?.({ view });
            return true;
          }

          return false;
        },
      },
      {
        key: 'ArrowDown',
        run: (view: EditorView) => {
          const range = view.state.field(popoverStateField)?.range;
          if (range) {
            options.onArrowDown?.({ view });
            return true;
          }

          return false;
        },
      },
      {
        key: 'Enter',
        run: (view: EditorView) => {
          const range = view.state.field(popoverStateField)?.range;
          if (range) {
            // The consumer deletes the range when it selects an item; when nothing is selectable
            // (empty menu over an `activateOnTyping` range) the key must fall through so the
            // typed text is preserved.
            return options.onEnter?.({ view }) ?? false;
          }

          return false;
        },
      },
    ].filter(isTruthy),
  );
};

/**
 * Creates a <dx-anchor> tag, which is used to anchor the Popver.
 */
const popoverAnchorDecoration = (options: PopoverOptions) => {
  return ViewPlugin.fromClass(
    class {
      _decorations: DecorationSet = Decoration.none;

      constructor(readonly view: EditorView) {}

      // TODO(wittjosiah): The decorations are repainted on every update, this occasionally causes menu to flicker.
      update({ view, transactions }: ViewUpdate) {
        const builder = new RangeSetBuilder<Decoration>();
        const { range, trigger } = view.state.field(popoverStateField) ?? {};
        if (range) {
          // Check if we should show the widget (only if cursor is within the active command range).
          const selection = view.state.selection.main;
          const showWidget = selection.head >= range.from && selection.head <= range.to;
          if (showWidget) {
            builder.add(
              range.from,
              range.to + 1,
              Decoration.mark({
                tagName: 'dx-anchor',
                class: 'cm-popover-trigger',
                attributes: {
                  'data-visible-focus': 'false',
                  'data-auto-trigger': 'true',
                  'data-trigger': trigger ?? options.triggerKey ?? '',
                },
              }),
            );
          }

          const rangeChanged = transactions.some((tr) => tr.effects.some((effect) => effect.is(popoverRangeEffect)));
          if (rangeChanged) {
            // NOTE: Content skips the trigger character.
            const content = view.state.sliceDoc(range.from + (trigger ? trigger.length : 0), range.to);
            options.onTextChange?.({ view, pos: selection.head, text: content, trigger });
          }
        }

        this._decorations = builder.finish();
      }
    },
    {
      decorations: (v) => v._decorations,
    },
  );
};

type PopoverState = {
  /**
   * Trigger prefix (in document).
   */
  trigger?: string;

  /**
   * Current document completion range.
   */
  range: Range;
};

// State effects for managing popover state.
export const popoverRangeEffect = StateEffect.define<PopoverState | null>();

// State field to track the active popover trigger range.
export const popoverStateField = StateField.define<PopoverState | null>({
  create: () => null,
  update: (value, tr) => {
    let newValue = value;
    for (const effect of tr.effects) {
      if (effect.is(popoverRangeEffect)) {
        newValue = effect.value;
      }
    }

    return newValue;
  },
});

const getLastIndexOf = (str: string, delimiters: string[]) =>
  Math.max(...delimiters.map((delim) => str.lastIndexOf(delim)));
